/**
 * Financial Goals Screen
 * Display all confirmed financial goals with progress tracking
 */

import React, { useEffect, useState, useRef, createRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  Modal,
  Alert,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { aiApi } from '../api/endpoints/ai';
import { useLayout } from '../components/layout/AppLayout';
import type { FinancialGoal, FinancialSubGoal } from '../api/types';
import ErrorMessage from '../components/common/ErrorMessage';
import ProgressBar from '../components/goals/ProgressBar';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import logger from '../utils/logger';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { analytics, AnalyticsEvent } from '../utils/analytics';

export default function FinancialGoalsScreen() {
  const isFocused = useIsFocused();
  const { navigationParams, clearNavigationParams, navigateTo } = useLayout();
  const scrollViewRef = useRef<ScrollView>(null);
  const goalRefs = useRef<{ [key: string]: View | null }>({});

  const [goals, setGoals] = useState<FinancialGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'active' | 'completed'>('active');

  // Update progress modal state
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<FinancialGoal | null>(null);
  const [newAmount, setNewAmount] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Completing roadmap step state
  const [completingStepId, setCompletingStepId] = useState<string | null>(null);

  // Reload goals when screen is focused or tab changes
  useEffect(() => {
    if (isFocused) {
      loadGoals();

      // Track financial goals screen viewed
      analytics.trackEvent(AnalyticsEvent.FINANCIAL_GOALS_VIEWED, {
        source: 'tab_navigation',
        status_filter: selectedTab,
      });
    }
  }, [isFocused, selectedTab]);

  // Handle scroll to specific goal when navigating from message
  useEffect(() => {
    const scrollToGoalId = navigationParams?.scrollToGoalId;

    if (scrollToGoalId && goals.length > 0 && !loading) {
      // Wait a bit for render to complete
      setTimeout(() => {
        const goalRef = goalRefs.current[scrollToGoalId];
        if (goalRef && scrollViewRef.current) {
          goalRef.measureLayout(
            scrollViewRef.current as any,
            (x, y) => {
              scrollViewRef.current?.scrollTo({ y: y - 20, animated: true });
              logger.log('[FinancialGoals] Scrolled to goal:', scrollToGoalId);
              // Clear params after scrolling
              clearNavigationParams();
            },
            () => logger.error('[FinancialGoals] Failed to measure goal position')
          );
        } else {
          logger.warn('[FinancialGoals] Goal not found:', scrollToGoalId);
        }
      }, 300);
    }
  }, [navigationParams?.scrollToGoalId, goals, loading]);

  const loadGoals = async () => {
    try {
      setError(null);
      logger.log(`[FinancialGoals] Loading ${selectedTab} goals...`);
      const response = await aiApi.getFinancialGoals(selectedTab);
      const loadedGoals = response.financial_goals || [];
      logger.log('[FinancialGoals] Loaded', loadedGoals.length, 'goals');

      // Backend already sorts by deadline (closest first)
      setGoals(loadedGoals);
    } catch (err: any) {
      logger.error('[FinancialGoals] Error loading financial goals:', err);
      logger.error('[FinancialGoals] Error details:', err.response?.data);
      setError('Erreur lors du chargement de vos objectifs financiers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadGoals();
  };

  const openUpdateModal = (goal: FinancialGoal) => {
    setSelectedGoal(goal);
    setNewAmount(goal.current_amount?.toString() || '0');
    setUpdateModalVisible(true);
  };

  const closeUpdateModal = () => {
    setUpdateModalVisible(false);
    setSelectedGoal(null);
    setNewAmount('');
  };

  // Handle completion of action steps for investment goals
  const handleCompleteActionStep = async (goalId: string, stepNumber: number, stepTitle: string) => {
    const confirmed = typeof window !== 'undefined' && window.confirm
      ? window.confirm(`Marquer "${stepTitle}" comme fait ?`)
      : await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Confirmer',
            `Marquer "${stepTitle}" comme fait ?`,
            [
              { text: 'Annuler', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Confirmer', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) {
      return;
    }

    setCompletingStepId(`${goalId}-${stepNumber}`);
    try {
      logger.log(`[FinancialGoals] Completing action step ${stepNumber} for goal ${goalId}`);
      const response = await aiApi.completeActionStep(goalId, stepNumber);
      logger.log('[FinancialGoals] Action step completed:', response);

      // Reload goals to get updated state
      await loadGoals();

      // Show success message
      const successMessage = response.is_all_steps_complete
        ? '🎉 Toutes les étapes complétées ! Tu es sur la voie de l\'investissement.'
        : `✅ Étape complétée ! ${response.next_step ? `Prochaine : ${response.next_step.title}` : ''}`;

      if (typeof window !== 'undefined' && window.alert) {
        window.alert(successMessage);
      } else {
        Alert.alert('Succès', successMessage);
      }

      // Track analytics
      analytics.trackEvent('action_step_completed', {
        goal_id: goalId,
        step_number: stepNumber,
        step_title: stepTitle,
        progress: response.progress,
        is_complete: response.is_all_steps_complete,
      });
    } catch (err: any) {
      logger.error('[FinancialGoals] Error completing action step:', err.response?.data || err.message);

      const errorMessage = err.response?.data?.error || err.message || 'Impossible de compléter cette étape';
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Erreur: ${errorMessage}`);
      } else {
        Alert.alert('Erreur', errorMessage);
      }
    } finally {
      setCompletingStepId(null);
    }
  };

  const handleCompleteStep = async (stepId: string, stepTitle: string) => {
    // Use window.confirm on web, Alert.alert on native
    const confirmed = typeof window !== 'undefined' && window.confirm
      ? window.confirm(`Marquer "${stepTitle}" comme terminé ?`)
      : await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Confirmer',
            `Marquer "${stepTitle}" comme terminé ?`,
            [
              { text: 'Annuler', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Confirmer', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) {
      return;
    }

    setCompletingStepId(stepId);
    try {
      logger.log(`[FinancialGoals] Completing step ${stepId}`);
      const response = await aiApi.completeRoadmapStep(stepId);
      logger.log('[FinancialGoals] Step completed:', response);

      // Reload goals to get updated state
      await loadGoals();

      // Show success message
      const successMessage = response.is_roadmap_complete
        ? '🎉 Félicitations ! Tu as complété toutes les étapes de ta roadmap !'
        : `✅ Étape complétée ! ${response.next_step ? `Prochaine étape : ${response.next_step.title}` : response.message}`;

      if (typeof window !== 'undefined' && window.alert) {
        window.alert(successMessage);
      } else {
        Alert.alert('Succès', successMessage);
      }

      // Track analytics
      analytics.trackEvent('roadmap_step_completed', {
        step_id: stepId,
        step_title: stepTitle,
        roadmap_progress: response.roadmap_progress,
        is_complete: response.is_roadmap_complete,
      });
    } catch (err: any) {
      logger.error('[FinancialGoals] Error completing step:', err.response?.data || err.message);

      const errorMessage = err.response?.data?.error || err.message || 'Impossible de compléter cette étape';
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Erreur: ${errorMessage}`);
      } else {
        Alert.alert('Erreur', errorMessage);
      }
    } finally {
      setCompletingStepId(null);
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedGoal || !selectedGoal.id) {
      Alert.alert('Erreur', 'Objectif non sélectionné');
      return;
    }

    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) {
      Alert.alert('Erreur', 'Veuillez entrer un montant valide');
      return;
    }

    setIsUpdating(true);
    try {
      logger.log(`[FinancialGoals] Updating progress for goal ${selectedGoal.id} to ${amount}€`);

      const response = await aiApi.updateGoalProgress(selectedGoal.id, amount);

      logger.log('[FinancialGoals] Update response:', response);

      // Update local goals list
      setGoals(prevGoals =>
        prevGoals.map(g =>
          g.id === selectedGoal.id ? { ...response.financial_goal } : g
        )
      );

      // Close modal immediately
      closeUpdateModal();

      // Track analytics
      analytics.trackEvent('financial_goal_progress_updated', {
        goal_id: selectedGoal.id,
        old_amount: selectedGoal.current_amount || 0,
        new_amount: amount,
        progress_percentage: response.financial_goal.progress_percentage,
      });

      // Show success message after modal closes
      setTimeout(() => {
        Alert.alert(
          'Succès',
          response.message || 'Progrès mis à jour avec succès !'
        );
      }, 300);
    } catch (err: any) {
      logger.error('[FinancialGoals] Error updating progress:', err);
      Alert.alert(
        'Erreur',
        err.response?.data?.message || 'Impossible de mettre à jour la progression'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const calculateMonthsRemaining = (deadline?: string): number | null => {
    if (!deadline) return null;

    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.44));

    return diffMonths > 0 ? diffMonths : 0;
  };

  const formatDeadline = (deadline?: string): string => {
    if (!deadline) return 'Non défini';

    const date = new Date(deadline);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };
    return date.toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return 'Non défini';
    return `${amount.toLocaleString('fr-FR')} €`;
  };

  const getGoalIcon = (type?: string): string => {
    switch (type) {
      case 'emergency_fund':
        return 'shield-check';
      case 'monthly_savings':
        return 'piggy-bank';
      case 'real_estate':
        return 'home';
      case 'investment':
        return 'chart-line';
      case 'debt_repayment':
      case 'debt_optimization':
        return 'credit-card-off';
      case 'retirement':
        return 'account-clock';
      case 'education':
        return 'school';
      case 'travel':
        return 'airplane';
      case 'budget_balance':
        return 'scale-balance';
      case 'expense_optimization':
        return 'scissors-cutting';
      case 'income_increase':
        return 'trending-up';
      default:
        return 'flag-checkered';
    }
  };

  const getGoalColor = (type?: string): string => {
    switch (type) {
      case 'emergency_fund':
        return colors.success;
      case 'monthly_savings':
        return colors.primary;
      case 'real_estate':
        return '#8B4513';
      case 'investment':
        return '#9333EA';
      case 'debt_repayment':
      case 'debt_optimization':
        return colors.error;
      case 'retirement':
        return '#059669';
      case 'education':
        return '#3B82F6';
      case 'travel':
        return '#EC4899';
      case 'budget_balance':
        return '#F59E0B';
      case 'expense_optimization':
        return '#10B981';
      case 'income_increase':
        return '#6366F1';
      default:
        return colors.textSecondary;
    }
  };

  const getSubGoalStatusIcon = (status: string): { icon: string; color: string } => {
    switch (status) {
      case 'completed':
        return { icon: 'check-circle', color: colors.success };
      case 'active':
        return { icon: 'play-circle', color: colors.primary };
      default:
        return { icon: 'circle-outline', color: colors.textSecondary };
    }
  };

  const getDifficultyLabel = (difficulty?: string): string => {
    switch (difficulty) {
      case 'easy':
        return '🟢 Facile';
      case 'medium':
        return '🟡 Moyen';
      case 'hard':
        return '🔴 Difficile';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Chargement de vos objectifs...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, selectedTab === 'active' && styles.activeTab]}
          onPress={() => setSelectedTab('active')}
        >
          <Text style={[styles.tabText, selectedTab === 'active' && styles.activeTabText]}>
            En cours
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, selectedTab === 'completed' && styles.activeTab]}
          onPress={() => setSelectedTab('completed')}
        >
          <Text style={[styles.tabText, selectedTab === 'completed' && styles.activeTabText]}>
            Complétés
          </Text>
        </TouchableOpacity>
      </View>

      {error && <ErrorMessage error={error} onDismiss={() => setError(null)} />}

      <ScrollView
        ref={scrollViewRef}
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {goals.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Icon
              name={selectedTab === 'completed' ? 'checkbox-marked-circle-outline' : 'target'}
              size={64}
              color={colors.textSecondary}
            />
            <Text style={styles.emptyTitle}>
              {selectedTab === 'completed' ? 'Aucun objectif complété' : 'Aucun objectif en cours'}
            </Text>
            <Text style={styles.emptyText}>
              {selectedTab === 'completed'
                ? 'Vos objectifs complétés apparaîtront ici'
                : 'Commencez une conversation avec Wealfi pour définir vos objectifs financiers'}
            </Text>
            {selectedTab !== 'completed' && (
              <TouchableOpacity
                style={styles.talkToWealfiButton}
                onPress={() => navigateTo('Chats')}
              >
                <Icon name="chat-processing-outline" size={24} color={colors.primary} />
                <Text style={styles.talkToWealfiText}>Parler à Wealfi</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <View style={styles.goalsContainer}>
            {goals.map((goal, index) => {
              const monthsRemaining = calculateMonthsRemaining(goal.deadline);
              const goalType = goal.financial_goal_type || goal.type;
              const iconName = getGoalIcon(goalType);
              const iconColor = getGoalColor(goalType);

              // Check if this goal has action_steps (structured steps like investment goals)
              // action_steps must be objects with step/title/status, not just strings
              const hasStructuredActionSteps = goal.action_steps &&
                Array.isArray(goal.action_steps) &&
                goal.action_steps.length > 0 &&
                typeof goal.action_steps[0] === 'object' &&
                'step' in (goal.action_steps[0] as any);

              // Check if this is a roadmap with sub_goals OR a goal with structured action_steps
              const isRoadmapWithSubGoals = goal.is_roadmap && goal.sub_goals && goal.sub_goals.length > 0;

              // Render as roadmap if it has sub_goals (traditional roadmap)
              if (isRoadmapWithSubGoals) {
                return (
                  <View
                    key={goal.id || index}
                    ref={(ref) => {
                      if (goal.id) {
                        goalRefs.current[goal.id] = ref;
                      }
                    }}
                    style={styles.roadmapCard}
                  >
                    {/* Roadmap Header */}
                    <View style={styles.roadmapHeader}>
                      <View style={[styles.goalIcon, { backgroundColor: iconColor + '20' }]}>
                        <Icon name="map-marker-path" size={24} color={iconColor} />
                      </View>
                      <View style={styles.roadmapHeaderText}>
                        <Text style={styles.roadmapTitle}>{goal.title}</Text>
                        <Text style={styles.roadmapSubtitle}>
                          🗺️ Roadmap • {goal.completed_steps || 0}/{goal.sub_goals_count || goal.sub_goals.length} étapes
                        </Text>
                      </View>
                    </View>

                    {/* Roadmap Progress */}
                    <View style={styles.roadmapProgress}>
                      <View style={styles.roadmapProgressBar}>
                        <View
                          style={[
                            styles.roadmapProgressFill,
                            { width: `${goal.roadmap_progress || 0}%` }
                          ]}
                        />
                      </View>
                      <Text style={styles.roadmapProgressText}>
                        {goal.roadmap_progress || 0}% complété
                      </Text>
                    </View>

                    {/* Sub-goals */}
                    <View style={styles.subGoalsContainer}>
                      {goal.sub_goals.map((subGoal, subIndex) => {
                        const statusInfo = getSubGoalStatusIcon(subGoal.status);
                        const subGoalType = subGoal.financial_goal_type;
                        const subIconName = getGoalIcon(subGoalType);
                        const subIconColor = getGoalColor(subGoalType);

                        return (
                          <View
                            key={subGoal.id || subIndex}
                            style={[
                              styles.subGoalItem,
                              subGoal.status === 'active' && styles.subGoalItemActive
                            ]}
                          >
                            <View style={styles.subGoalHeader}>
                              <Icon
                                name={statusInfo.icon}
                                size={20}
                                color={statusInfo.color}
                              />
                              <Text style={[
                                styles.subGoalPosition,
                                subGoal.status === 'completed' && styles.subGoalPositionCompleted
                              ]}>
                                {subGoal.position}.
                              </Text>
                              <View style={[styles.subGoalIcon, { backgroundColor: subIconColor + '15' }]}>
                                <Icon name={subIconName} size={16} color={subIconColor} />
                              </View>
                              <Text style={[
                                styles.subGoalTitle,
                                subGoal.status === 'completed' && styles.subGoalTitleCompleted
                              ]}>
                                {subGoal.title}
                              </Text>
                            </View>

                            {/* Impact data */}
                            {subGoal.impact_data?.monthly_gain && (
                              <View style={styles.subGoalImpact}>
                                <Text style={styles.subGoalImpactText}>
                                  💰 +{subGoal.impact_data.monthly_gain}€/mois
                                </Text>
                                {subGoal.impact_data.difficulty && (
                                  <Text style={styles.subGoalDifficulty}>
                                    {getDifficultyLabel(subGoal.impact_data.difficulty)}
                                  </Text>
                                )}
                              </View>
                            )}

                            {/* Action steps for active sub-goal */}
                            {subGoal.status === 'active' && subGoal.action_steps && subGoal.action_steps.length > 0 && (
                              <View style={styles.actionStepsContainer}>
                                <Text style={styles.actionStepsTitle}>Actions :</Text>
                                {subGoal.action_steps.slice(0, 3).map((step, stepIndex) => (
                                  <View key={stepIndex} style={styles.actionStepItem}>
                                    <Text style={styles.actionStepBullet}>•</Text>
                                    <Text style={styles.actionStepText}>{step}</Text>
                                  </View>
                                ))}
                              </View>
                            )}

                            {/* Complete step button for active sub-goal */}
                            {subGoal.status === 'active' && (
                              <TouchableOpacity
                                style={styles.completeStepButton}
                                onPress={() => handleCompleteStep(subGoal.id, subGoal.title)}
                                disabled={completingStepId === subGoal.id}
                              >
                                {completingStepId === subGoal.id ? (
                                  <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                  <>
                                    <Icon name="check" size={18} color="#FFFFFF" />
                                    <Text style={styles.completeStepButtonText}>
                                      Marquer comme terminé
                                    </Text>
                                  </>
                                )}
                              </TouchableOpacity>
                            )}
                          </View>
                        );
                      })}
                    </View>

                    {/* Total Impact */}
                    {goal.target_amount && goal.target_amount > 0 && (
                      <View style={styles.roadmapTotal}>
                        <Text style={styles.roadmapTotalLabel}>Impact total visé :</Text>
                        <Text style={styles.roadmapTotalValue}>
                          +{formatCurrency(goal.target_amount)}/mois
                        </Text>
                      </View>
                    )}
                  </View>
                );
              }

              // Render as action steps roadmap if it has structured action_steps (any goal type)
              if (hasStructuredActionSteps) {
                const completedSteps = goal.action_steps.filter((s: any) => s.status === 'completed').length;
                const totalSteps = goal.action_steps.length;
                const progressPercent = Math.round((completedSteps / totalSteps) * 100);

                return (
                  <View
                    key={goal.id || index}
                    ref={(ref) => {
                      if (goal.id) {
                        goalRefs.current[goal.id] = ref;
                      }
                    }}
                    style={[styles.actionStepsRoadmapCard, { borderColor: iconColor }]}
                  >
                    {/* Header */}
                    <View style={styles.roadmapHeader}>
                      <View style={[styles.goalIcon, { backgroundColor: iconColor + '20' }]}>
                        <Icon name={iconName} size={24} color={iconColor} />
                      </View>
                      <View style={styles.roadmapHeaderText}>
                        <Text style={styles.roadmapTitle}>{goal.title}</Text>
                        <Text style={styles.roadmapSubtitle}>
                          📋 Plan d'action • {completedSteps}/{totalSteps} étapes
                        </Text>
                      </View>
                    </View>

                    {/* Monthly effort */}
                    {goal.monthly_effort !== undefined && goal.monthly_effort > 0 && (
                      <View style={[styles.actionStepsMonthlyEffort, { backgroundColor: iconColor + '15' }]}>
                        <Icon name="calendar-month" size={18} color={iconColor} />
                        <Text style={[styles.actionStepsMonthlyEffortText, { color: iconColor }]}>
                          {formatCurrency(goal.monthly_effort)}/mois
                        </Text>
                      </View>
                    )}

                    {/* Progress bar */}
                    <View style={styles.roadmapProgress}>
                      <View style={styles.roadmapProgressBar}>
                        <View
                          style={[
                            styles.actionStepsProgressFill,
                            { width: `${progressPercent}%`, backgroundColor: iconColor }
                          ]}
                        />
                      </View>
                      <Text style={styles.roadmapProgressText}>
                        {progressPercent}% complété
                      </Text>
                    </View>

                    {/* Action steps */}
                    <View style={styles.subGoalsContainer}>
                      {goal.action_steps.map((step: any, stepIndex: number) => {
                        const isCompleted = step.status === 'completed';
                        const isActive = step.status === 'pending' &&
                          (stepIndex === 0 || goal.action_steps[stepIndex - 1]?.status === 'completed');
                        const statusInfo = isCompleted
                          ? { icon: 'check-circle', color: colors.success }
                          : isActive
                            ? { icon: 'play-circle', color: colors.primary }
                            : { icon: 'circle-outline', color: colors.textSecondary };

                        return (
                          <View
                            key={step.step || stepIndex}
                            style={[
                              styles.subGoalItem,
                              isActive && styles.subGoalItemActive
                            ]}
                          >
                            <View style={styles.subGoalHeader}>
                              <Icon
                                name={statusInfo.icon}
                                size={20}
                                color={statusInfo.color}
                              />
                              <Text style={[
                                styles.subGoalPosition,
                                isCompleted && styles.subGoalPositionCompleted
                              ]}>
                                {step.step}.
                              </Text>
                              <Text style={[
                                styles.subGoalTitle,
                                isCompleted && styles.subGoalTitleCompleted
                              ]}>
                                {step.title}
                              </Text>
                            </View>

                            {/* Description */}
                            {step.description && (
                              <View style={styles.actionStepDescription}>
                                <Text style={styles.actionStepDescriptionText}>
                                  {step.description}
                                </Text>
                              </View>
                            )}

                            {/* Due date */}
                            {step.due_date && !isCompleted && (
                              <View style={styles.actionStepDueDate}>
                                <Icon name="clock-outline" size={14} color={colors.textSecondary} />
                                <Text style={styles.actionStepDueDateText}>
                                  À faire avant le {new Date(step.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </Text>
                              </View>
                            )}

                            {/* Complete button for active step */}
                            {isActive && goal.id && (
                              <TouchableOpacity
                                style={[styles.completeActionStepButton, { backgroundColor: iconColor }]}
                                onPress={() => handleCompleteActionStep(goal.id!, step.step, step.title)}
                                disabled={completingStepId === `${goal.id}-${step.step}`}
                              >
                                {completingStepId === `${goal.id}-${step.step}` ? (
                                  <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                  <>
                                    <Icon name="check" size={16} color="#FFFFFF" />
                                    <Text style={styles.completeStepButtonText}>
                                      Marquer comme fait
                                    </Text>
                                  </>
                                )}
                              </TouchableOpacity>
                            )}
                          </View>
                        );
                      })}
                    </View>

                    {/* Projection (for investment goals) */}
                    {goal.roadmap_data?.projection_12_months && (
                      <View style={styles.actionStepsProjection}>
                        <Text style={styles.actionStepsProjectionLabel}>📊 Projection dans 12 mois :</Text>
                        <Text style={[styles.actionStepsProjectionValue, { color: iconColor }]}>
                          {formatCurrency(goal.roadmap_data.projection_12_months)}
                        </Text>
                      </View>
                    )}
                  </View>
                );
              }

              // Regular goal card (non-roadmap, no action_steps)
              return (
                <View
                  key={goal.id || index}
                  ref={(ref) => {
                    if (goal.id) {
                      goalRefs.current[goal.id] = ref;
                    }
                  }}
                  style={styles.goalCard}
                >
                  <View style={styles.goalHeader}>
                    <View style={[styles.goalIcon, { backgroundColor: iconColor + '20' }]}>
                      <Icon name={iconName} size={24} color={iconColor} />
                    </View>
                    <Text style={styles.goalTitle}>{goal.title}</Text>
                  </View>

                  <View style={styles.goalDetails}>
                    {goal.target_amount !== undefined && (
                      <View style={styles.detailRow}>
                        <Icon name="currency-eur" size={20} color={colors.textSecondary} />
                        <Text style={styles.detailLabel}>Montant cible :</Text>
                        <Text style={styles.detailValue}>
                          {formatCurrency(goal.target_amount)}
                        </Text>
                      </View>
                    )}

                    {goal.monthly_effort !== undefined && (
                      <View style={styles.detailRow}>
                        <Icon name="calendar-month" size={20} color={colors.textSecondary} />
                        <Text style={styles.detailLabel}>Épargne mensuelle :</Text>
                        <Text style={styles.detailValue}>
                          {formatCurrency(goal.monthly_effort)}
                        </Text>
                      </View>
                    )}

                    {goal.deadline && (
                      <View style={styles.detailRow}>
                        <Icon name="calendar-clock" size={20} color={colors.textSecondary} />
                        <Text style={styles.detailLabel}>Échéance :</Text>
                        <Text style={styles.detailValue}>
                          {formatDeadline(goal.deadline)}
                          {monthsRemaining !== null && (
                            <Text style={styles.monthsRemaining}>
                              {' '}({monthsRemaining} mois)
                            </Text>
                          )}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Progress Bar */}
                  {goal.target_amount !== undefined && (
                    <View style={styles.progressSection}>
                      <ProgressBar
                        current={goal.current_amount || 0}
                        target={goal.target_amount}
                        showAmounts={true}
                      />
                    </View>
                  )}

                  {/* Update Progress Button - only show if goal not yet reached */}
                  {!goal.is_goal_reached && (
                    <TouchableOpacity
                      style={styles.updateButton}
                      onPress={() => openUpdateModal(goal)}
                    >
                      <Icon name="pencil" size={18} color={colors.primary} />
                      <Text style={styles.updateButtonText}>Mettre à jour ma progression</Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      {/* Update Progress Modal */}
      <Modal
        visible={updateModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={closeUpdateModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Mettre à jour la progression</Text>
              <TouchableOpacity onPress={closeUpdateModal}>
                <Icon name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {selectedGoal && (
              <>
                <Text style={styles.modalGoalTitle}>{selectedGoal.title}</Text>

                <View style={styles.modalStats}>
                  <View style={styles.modalStat}>
                    <Text style={styles.modalStatLabel}>Objectif</Text>
                    <Text style={styles.modalStatValue}>
                      {formatCurrency(selectedGoal.target_amount)}
                    </Text>
                  </View>
                  <View style={styles.modalStat}>
                    <Text style={styles.modalStatLabel}>Actuel</Text>
                    <Text style={styles.modalStatValue}>
                      {formatCurrency(selectedGoal.current_amount)}
                    </Text>
                  </View>
                </View>

                <Text style={styles.inputLabel}>Nouveau montant épargné (€)</Text>
                <TextInput
                  style={styles.input}
                  value={newAmount}
                  onChangeText={setNewAmount}
                  keyboardType="numeric"
                  placeholder="Ex: 2500"
                  placeholderTextColor={colors.textSecondary}
                />

                <View style={styles.modalActions}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.cancelButton]}
                    onPress={closeUpdateModal}
                  >
                    <Text style={styles.cancelButtonText}>Annuler</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.modalButton, styles.confirmButton, isUpdating && styles.confirmButtonDisabled]}
                    onPress={handleUpdateProgress}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.confirmButtonText}>Confirmer</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  tabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tab: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.xs,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.backgroundSecondary,
  },
  activeTab: {
    backgroundColor: colors.primary,
  },
  tabText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
  },
  activeTabText: {
    color: colors.background,
    fontWeight: typography.fontWeight.bold,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  content: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    marginTop: spacing.xxl,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  talkToWealfiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  talkToWealfiText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  goalsContainer: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  goalCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  goalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  goalIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  goalTitle: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  goalDetails: {
    gap: spacing.sm,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  detailLabel: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    marginLeft: spacing.sm,
    flex: 1,
  },
  detailValue: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  monthsRemaining: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.regular,
  },
  progressSection: {
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  updateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  updateButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold,
    marginLeft: spacing.xs,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  modalGoalTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  modalStats: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  modalStat: {
    flex: 1,
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: spacing.sm,
    alignItems: 'center',
  },
  modalStatLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  modalStatValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  inputLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  input: {
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginBottom: spacing.lg,
  },
  modalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  modalButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  confirmButton: {
    backgroundColor: colors.primary,
  },
  confirmButtonDisabled: {
    opacity: 0.5,
  },
  confirmButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: '#FFFFFF',
  },
  // Roadmap styles
  roadmapCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 2,
    borderColor: '#F59E0B',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  roadmapHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  roadmapHeaderText: {
    flex: 1,
    marginLeft: spacing.md,
  },
  roadmapTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  roadmapSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  roadmapProgress: {
    marginBottom: spacing.md,
  },
  roadmapProgressBar: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
  },
  roadmapProgressFill: {
    height: '100%',
    backgroundColor: '#F59E0B',
    borderRadius: 4,
  },
  roadmapProgressText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    textAlign: 'right',
  },
  subGoalsContainer: {
    gap: spacing.sm,
  },
  subGoalItem: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: spacing.md,
    borderLeftWidth: 3,
    borderLeftColor: colors.border,
  },
  subGoalItemActive: {
    borderLeftColor: colors.primary,
    backgroundColor: colors.primary + '08',
  },
  subGoalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subGoalPosition: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginLeft: spacing.xs,
    marginRight: spacing.sm,
    width: 20,
  },
  subGoalPositionCompleted: {
    color: colors.textSecondary,
  },
  subGoalIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  subGoalTitle: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  subGoalTitleCompleted: {
    color: colors.textSecondary,
    textDecorationLine: 'line-through',
  },
  subGoalImpact: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
    marginLeft: 48,
    gap: spacing.md,
  },
  subGoalImpactText: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    fontWeight: typography.fontWeight.semibold,
  },
  subGoalDifficulty: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  actionStepsContainer: {
    marginTop: spacing.sm,
    marginLeft: 48,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  actionStepsTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  actionStepItem: {
    flexDirection: 'row',
    marginTop: 4,
  },
  actionStepBullet: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginRight: spacing.xs,
  },
  actionStepText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  roadmapTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  roadmapTotalLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  roadmapTotalValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
  },
  completeStepButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.success,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.md,
    marginLeft: 48,
  },
  completeStepButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
    marginLeft: spacing.xs,
  },
  // Goals with action_steps (rendered as mini-roadmap)
  actionStepsRoadmapCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 2,
    // borderColor is set dynamically based on goal type
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  actionStepsMonthlyEffort: {
    flexDirection: 'row',
    alignItems: 'center',
    // backgroundColor is set dynamically
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  actionStepsMonthlyEffortText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    // color is set dynamically
    marginLeft: spacing.sm,
  },
  actionStepsProgressFill: {
    height: '100%',
    // backgroundColor is set dynamically
    borderRadius: 4,
  },
  actionStepDescription: {
    marginTop: spacing.xs,
    marginLeft: 28, // Align with title after icon
  },
  actionStepDescriptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  actionStepDueDate: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
    marginLeft: 28,
  },
  actionStepDueDateText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginLeft: 4,
  },
  completeActionStepButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    // backgroundColor is set dynamically
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
    marginLeft: 28,
  },
  actionStepsProjection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  actionStepsProjectionLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  actionStepsProjectionValue: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    // color is set dynamically
  },
});
